import { ColumnTypes, TRootOptions } from '@/components';
import { EditableCellType } from '@/components/common/table/type';
import {
	AGRI_FRANCHISE_OPTIONS,
	ALL_DOCUMENT_OPTIONS,
	ALLOCATION_RESTRICTED_OPTIONS,
	AnyObject,
	APPLICANT_TYPE_OPTION,
	AppProgramLabel,
	AppProgramStatusEnum,
	AreaRefTableNameEnum,
	BUSINESS_TYPE_OPTIONS,
	CARO_OPTIONS,
	CaroEnum,
	DIVISION_RECOMMENDER_OPTIONS,
	EXTEND_STAY_OPTIONS,
	FILE_TYPE_OPTIONS,
	FISHERY_FRANCHISE_OPTIONS,
	GENDER_OPTION,
	GRADE_OPTIONS,
	GradeSalaryEnum,
	INFRASTRUCTURE_OPTIONS,
	IsAllocationRestrictedEnum,
	LODGING_PROVIDER_TYPE_OPTIONS,
	MasterInfoEnum,
	MidTermDepartureEnum,
	NATIONAL_LIST,
	OPTIONS_DETAIL_REASON,
	OPTIONS_REASON,
	OWNERSHIP_OPTIONS,
	PageSize,
	PageTypeEnum,
	PRODUCTION_TYPE_AGRICULTURE_OPTIONS,
	PRODUCTION_TYPE_FISHING_OPTIONS,
	PRODUCTION_UNIT_AGRICULTURE_OPTIONS,
	PRODUCTION_UNIT_FISHING_OPTIONS,
	PROVIDERS_TYPE_LODGING_OPTIONS,
	RANGE_AGE_OPTIONS,
	RangeAgeEnum,
	RE_ENTRY_RECOMMENDATION_STATUS_OPTIONS,
	ReEntryRecommendationStatusEnum,
	RELATIONSHIP_OPTIONS,
	RELAX_TIME_OPTIONS,
	RESIDENCE_TYPE_OPTION,
	TListOption,
	UNIT_TYPES,
	VISA_CODE_OPTION,
	VISA_TIME_OPTIONS,
} from '@/constant';
import { IWorkerSalary, TGetDataList, TPayloadUpdateSuccess } from '@/services';
import {
	dayjs,
	formatDate,
	getDiffExpirationDate,
	jsonToUrlParams,
	showDataGridWithSymbol,
	SUPPORT_FORMAT_DATE_LIST,
} from '@/utils';
import { ReactNode, RefObject, useEffect, useRef } from 'react';

import { type ClassValue, clsx } from 'clsx';
import i18next, { exists, Namespace, TFunction, TOptions } from 'i18next';
import { all, any, equals, has, includes, isNotNil, last, length } from 'ramda';
import { isArray, isNilOrEmpty, isString, isValidNumber } from 'ramda-adjunct';
import { twMerge } from 'tailwind-merge';

export const cn = (...inputs: ClassValue[]) => {
	return twMerge(clsx(inputs));
};

export const map = <T, S>(
	input: T[] | undefined,
	callback: (value: T, index: number, array: T[]) => S
): S[] => {
	if (!isArray(input)) return [];
	return input.map(callback);
};

export const filter = <T, S>(
	input: T[] | undefined,
	callback: (value: T, index: number, array: T[]) => S
): T[] => {
	if (!isArray(input)) return [];
	return input.filter(callback);
};

export const insertIf = <T>(condition: boolean, ...elements: T[]) => {
	return condition ? [...elements] : [];
};

export const insertObjectIf = <T1 extends object>(
	condition: boolean | unknown,
	elements1: T1
): Partial<T1> => {
	return condition ? elements1 : ({} as T1);
};

export const insertObjectIfV2 = <T1>(
	condition: boolean | any,
	elements1: T1
): Partial<T1> => {
	return condition ? elements1 : ({} as T1);
};

export const insertObjectIfElse = <T1, T2>(
	condition: boolean,
	elements1: T1,
	elements2: T2
): Partial<T1 | T2> => {
	return condition ? elements1 : elements2;
};

export const cls = (...classes: Array<string | boolean | undefined>) => {
	return classes.filter(Boolean).join(' ');
};

export const deepCompare = (obj1: AnyObject, obj2: AnyObject) => {
	if (
		typeof obj1 === 'object' &&
		obj1 !== null &&
		typeof obj2 === 'object' &&
		obj2 !== null
	) {
		const keys1 = Object.keys(obj1);
		const keys2 = Object.keys(obj2);

		if (keys1.length !== keys2.length) {
			return false;
		}

		for (const key of keys1) {
			if (!keys2.includes(key) || !deepCompare(obj1[key], obj2[key])) {
				return false;
			}
		}

		return true;
	}

	return obj1 === obj2;
};

export const compareCommonProperties = (
	obj1: AnyObject,
	obj2: AnyObject
): boolean => {
	if (!obj1 || !obj2) return false;
	const commonKeys = Object.keys(obj1).filter((key) =>
		Object.prototype.hasOwnProperty.call(obj2, key)
	);

	for (const key of commonKeys) {
		if (
			typeof obj1[key as keyof typeof obj1] === 'object' &&
			obj1[key as keyof typeof obj1] !== null &&
			typeof obj2[key as keyof typeof obj2] === 'object' &&
			obj2[key as keyof typeof obj2] !== null
		) {
			// Nếu giá trị là object, gọi đệ quy
			if (
				!compareCommonProperties(
					obj1[key as keyof typeof obj1],
					obj2[key as keyof typeof obj2]
				)
			) {
				return false;
			}
		} else if (
			obj1[key as keyof typeof obj1] !== obj2[key as keyof typeof obj2]
		) {
			return false;
		}
	}

	return true;
};

export const getNoNumber = (data: TGetDataList<object>, index: number) => {
	return (
		Number(data?.total) -
		Number(data?.page - 1) * Number(data?.pageSize) -
		index
	);
};

export const getPagePagination = (total: number = 0, pageSize = PageSize.S) => {
	return total % pageSize === 0
		? Math.floor(total / pageSize)
		: Math.floor(total / pageSize) + 1;
};

export const isFile = (f: unknown) =>
	f instanceof File && typeof f.name == 'string';

export const isJSON = (text: unknown) => {
	if (typeof text !== 'string') {
		return false;
	}
	try {
		JSON.parse(text);
		return true;
	} catch (error) {
		return false;
	}
};

export const NOOP = () => {
	//
};

export const truncateFileName = (fileName: string, maxLength = 20) => {
	if (!fileName) return;
	const names = fileName?.split('.');
	const type = last(names);

	const name = fileName?.split(`.${type}`)?.[0];

	if (length(name) > maxLength) {
		return name.substring(0, maxLength) + '...' + type;
	}

	return fileName;
};

const usedIds = new Set<number>();

export const randomId = (): number => {
	let id: number;

	do {
		id = Number(
			`${Date.now()}${Math.floor(Math.random() * 1000)
				.toString()
				.padStart(3, '0')}`
		);
	} while (usedIds.has(id));

	usedIds.add(id);
	return id;
};

export const safeJsonParse = <T>(jsonString: string): T | null => {
	try {
		return JSON.parse(jsonString) as T;
	} catch (error) {
		return null;
	}
};

export const delay = (ms: number) => {
	return new Promise((resolve) => setTimeout(resolve, ms));
};

export const navigateCreateOrDetail = (
	pathname: string,
	id?: string | number,
	query?: any
) => {
	if (!window && isNilOrEmpty(pathname)) return;

	const detailSuffix = ':id';

	let features =
		'height=1080,width=1920,toolbar=1,Location=0,Directories=0,Status=0,menubar=1,Scrollbars=1,Resizable=1';

	const url =
		window.origin + includes(detailSuffix, pathname)
			? pathname.replace(detailSuffix, String(id)) +
				(query ? `?${jsonToUrlParams(query)}` : '')
			: pathname + (query ? `?${jsonToUrlParams(query)}` : '');

	return window.open(url, '_blank', features);

	// As Parent
	// const popup = navigateDetail(ROUTERS., id);
	// 	if (popup) {
	// 		popup.addEventListener(
	// 			'message',
	// 			(event) => {
	// 				//TODO: do some thing(event.data, 'event.data');
	// 			},
	// 			false
	// 		);
	// 	}

	// As Children
	// 	window.postMessage({ response: 'Success!' });
	// 	window.close();
};

export const getDiffObject = <T extends { [key: string]: any }>(
	obj1: T,
	obj2: T
): Partial<T> => {
	return Object.keys(obj1).reduce((diff, key) => {
		if (key.startsWith('has') && key.endsWith('ServerError')) {
			return diff;
		}

		if (Array.isArray(obj2[key])) {
			if (equals(obj2[key], obj1[key])) {
				return diff;
			}
		} else {
			if (obj2[key] === obj1[key]) return diff;
		}

		return {
			...diff,
			[key]: obj1[key],
		};
	}, {});
};

export const convertCamelToServerKey = (prefixKey: string, key: string) => {
	const capitalizedKey = key.charAt(0).toUpperCase() + key.slice(1);

	const convertedKey = `${prefixKey}${capitalizedKey}`;

	return convertedKey;
};

type WithKeyFlags<T, P extends string> = T & {
	[K in keyof T as `${P}${Capitalize<string & K>}`]: string;
};

export const injectedFieldServerKey = <
	T extends {
		[key: string]: any;
	},
	P extends string,
>(
	initialValues: T,
	prefixKey: string
): WithKeyFlags<T, P> => {
	const result = {} as T;

	for (const [key, value] of Object.entries(initialValues)) {
		if (Object.hasOwn(initialValues, key)) {
			const convertedKey = convertCamelToServerKey(prefixKey, key);
			//@ts-expect-error: ignore error
			result[convertedKey] = value;
		}
	}

	return result;
};

export const findPosition = (arr: number[], target: number) => {
	if (target > arr[arr.length - 1]) {
		return arr.length;
	}

	for (let i = 1; i < arr.length; i++) {
		if (arr[i - 1] < target && target < arr[i]) {
			return i;
		}
	}

	return -1;
};

export const mergeListItemUpdated = <T1, T2>(
	list: T1[],
	updatedData: TPayloadUpdateSuccess<T2>['data'],
	key: string = 'id',
	keyUpdateDt: string = 'updateDt'
): T1[] => {
	return list.map((e: T1) => {
		if (e[key as keyof T1] === updatedData[key as keyof T2]) {
			return mergeItemUpdated<T1, T2>(e, updatedData) as T1;
		}
		return e as T1;
	}) as T1[];
};

export const mergeItemUpdated = <T1, T2>(
	oldData: T1,
	updatedData: TPayloadUpdateSuccess<T2>['data'],
	keyUpdateDt: string = 'updateDt'
): T1 => {
	let newData = { ...oldData } as T1;
	Object.keys(updatedData).map((key: string) => {
		let value: string | number | undefined = updatedData?.[key as keyof T2];
		if (
			isValidNumber(Number(value)) &&
			typeof newData[key as keyof T1] === 'number'
		) {
			value = Number(value);
		}
		newData = {
			...newData,
			[key as keyof T1]: value,
		};
	});

	return {
		...newData,
		[keyUpdateDt]: formatDate(dayjs()),
	};
};

export const checkElementContain = (
	el: HTMLElement,
	className: string
): boolean => {
	const classList = el?.classList;

	return classList?.value?.includes('ant-picker');
};

export const generateFilterItem = (
	options: TListOption[],
	startIndex: number = 0,
	isDynamic: boolean = true
): TRootOptions[] =>
	options
		.filter((e: TListOption) => e.filter !== false)
		.map(
			(e: TListOption, index: number): TRootOptions => ({
				order: startIndex + index + 1,
				...insertObjectIf(e?.filterItem, {
					filterItem: e?.filterItem,
				}),
				static: isDynamic,
				prefixKey: e.prefixKey,
				label: e.title,
				value: e.filterKey || e.key,
			})
		);

export const getColumnItem = (
	e: TListOption,
	option?: { [key: string]: any }
): ColumnTypes[number] => ({
	...e,
	title: e.title,
	dataIndex: e.key,
	key: e.key,
	align: 'center',
	...insertObjectIf(e.editable, {
		editable: e.editable,
		cellType: e.cellType || EditableCellType.TEXT_INPUT,
		dateProps: e.dateProps,
		dropdownProps: e.dropdownProps,
		inputProps: e.inputProps || {},
	}),
	...insertObjectIf(e.render, {
		render: e.render,
	}),
	...insertObjectIf(e.width, {
		width: e.width,
	}),
	...insertObjectIf(!e.width && option?.width, {
		width: option?.width,
	}),
	...insertObjectIf(e.fixed, {
		fixed: e.fixed,
	}),
});

export const isFunction = (
	item: any
): item is (data: any, index?: number) => any => {
	return typeof item === 'function';
};

export const convertNationCodeToName = (nationality: string): string => {
	if (!nationality) return '';
	const found = NATIONAL_LIST.find((e) => e?.value === nationality);
	return found ? found?.label : nationality;
};

export const convertGenderCodeToName = (gender: string): string => {
	if (!gender) return '';
	const found = GENDER_OPTION.find((e) => e?.value === gender);
	if (!found) return gender;
	return i18next.t(found.label as any, { ns: 'common' });
};

export const convertGradeCodeToName = (grade: string): string => {
	if (!grade) return '';
	const found = GRADE_OPTIONS.find((e) => e?.value === grade);
	if (!found) return grade;
	return i18next.t(found.label as any, { ns: 'common' });
};

export const convertApplicantTypeCodeToName = (
	applicantType: string
): string => {
	if (!applicantType) return '';
	const found = APPLICANT_TYPE_OPTION.find((e) => e?.value === applicantType);
	return found ? found?.label : applicantType;
};

export const convertVisaCodeToName = (visaCode: string): string => {
	if (!visaCode) return '';
	const found = VISA_CODE_OPTION.find((e) => e?.value === visaCode);
	return found ? found?.label : visaCode;
};

export const convertVisaTimeToName = (visaTime: string): string => {
	if (!visaTime) return '';
	const found = VISA_TIME_OPTIONS.find((e) => e?.value === visaTime);
	return found ? found?.label : visaTime;
};

export const convertDocumentTypeToName = (documentType: string): string => {
	if (!documentType) return '';
	const found = ALL_DOCUMENT_OPTIONS.find((e) => e?.value === documentType);
	return found ? String(found?.label) : documentType;
};

export const convertExtensionPeriodToName = (
	extensionPeriod: string
): string => {
	if (!extensionPeriod) return '';
	const found = EXTEND_STAY_OPTIONS.find((e) => e?.value === extensionPeriod);
	if (!found) return extensionPeriod;
	return i18next.t(found.label as any, { ns: 'common' });
};

export const convertReEntryRecommendationStatus = (
	reEntryRecommendationStatus: ReEntryRecommendationStatusEnum
): string => {
	if (!reEntryRecommendationStatus) return '';
	const found = RE_ENTRY_RECOMMENDATION_STATUS_OPTIONS.find(
		(e) => e?.value === reEntryRecommendationStatus
	);
	return found ? found?.label : reEntryRecommendationStatus;
};

export const convertResidenceTypeCodeToName = (
	residenceType: string
): string => {
	if (!residenceType) return '';
	const found = RESIDENCE_TYPE_OPTION.find((e) => e?.value === residenceType);
	return found ? found?.label : residenceType;
};

export const convertCaroEnumToName = (
	caro: number | boolean | CaroEnum | string
): string => {
	if (!isNotNil(caro)) return '';
	const found = CARO_OPTIONS.find(
		(e: any) => Number(e?.value) === Number(caro)
	);
	return String(found ? found?.label : caro);
};

export const convertAllocationRestrictedToName = (
	allocation: boolean | IsAllocationRestrictedEnum | string | number
): boolean | IsAllocationRestrictedEnum | string | number => {
	if (!isNotNil(allocation)) return '';
	const found = ALLOCATION_RESTRICTED_OPTIONS.find(
		(e) => e?.value === allocation
	);
	return found ? found?.label : allocation;
};

export const convertExpirationDate = (expiration: string | null) => {
	if (!expiration) return '';
	const diffExpirationDate = getDiffExpirationDate(expiration);
	const value =
		diffExpirationDate < 0
			? formatDate(expiration)
			: `[D-${diffExpirationDate}] ${formatDate(expiration)}`;
	return value;
};

export const convertDropdownRelation = (type: MasterInfoEnum) => {
	switch (type) {
		case MasterInfoEnum.APPLICANT:
			return PageTypeEnum.APPLICANT;
		case MasterInfoEnum.FRANCHISE:
			return PageTypeEnum.FRANCHISE;
		case MasterInfoEnum.RECOMMENDER:
			return PageTypeEnum.RECOMMENDER;
		case MasterInfoEnum.LODGING:
			return PageTypeEnum.LODGING;
		default:
			return '';
	}
};

export const dateDuration = (startDate: string, endDate: string) => {
	if (!startDate && !endDate) return '';
	const startDateTime = new Date(startDate).getTime();
	const endDateTime = new Date(endDate).getTime();
	if (Number.isNaN(startDateTime) && Number.isNaN(endDateTime)) return '';
	return `${Math.floor(Math.abs((startDateTime - endDateTime) / (24 * 3600 * 1000))) + 1}`;
};

export const getApplicantDateRange = (entryDate: any, departureDate: any) => {
	if (!isNilOrEmpty(entryDate) && !isNilOrEmpty(departureDate))
		return entryDate + ' ~ ' + departureDate;
	return '';
};

export const convertOwnershipCodeToName = (ownership: string): string => {
	const found = OWNERSHIP_OPTIONS.find((e) => e?.value === ownership);
	return found ? found?.label : ownership;
};

export const convertInfrastructureCodeToName = (
	infrastructure: string
): string => {
	const found = INFRASTRUCTURE_OPTIONS.find((e) => e?.value === infrastructure);
	return found ? found?.label : infrastructure;
};

export const convertLodgingProviderTypeCodeToName = (
	providerType: string
): string => {
	const found = LODGING_PROVIDER_TYPE_OPTIONS.find(
		(e) => e?.value === providerType
	);
	return found ? found?.label : providerType;
};

export const convertFranchiseGradeCodeToName = (grade: IWorkerSalary) => {
	const gradeCode = grade?.applicantSalaryGrade?.replace?.('GRADE_', '') || '';

	return showDataGridWithSymbol(
		gradeCode,
		grade?.applicantSalaryHourlyWage?.toLocaleString?.('ko-KR') ??
			GradeSalaryEnum[
				grade?.applicantSalaryGrade as keyof typeof GradeSalaryEnum
			] ??
			GradeSalaryEnum.GRADE_B,
		'/'
	);
};

export const convertReasonCodeToName = (reason: string): string => {
	const found = OPTIONS_REASON.find((e) => e?.value === reason);
	return found ? found?.label : reason;
};

export const convertReasonDettailCodeToName = (reason: string): string => {
	const found = OPTIONS_DETAIL_REASON.find((e) => e?.value === reason);
	return found ? found?.label : reason;
};

export const arrayToJson = <T = { [key: string]: any }>(
	array: T[] = [],
	key: keyof T
): {
	[key: string]: T;
} => {
	try {
		if (isNilOrEmpty(array)) return {};

		const result = {};

		for (const item of array) {
			// eslint-disable-next-line @typescript-eslint/ban-ts-comment
			// @ts-ignore
			result[item[key]] = item;
		}

		return result;
	} catch {
		return {};
	}
};

export const arrayStringToJson = (
	array: string[]
): { [key: string]: string } => {
	return array.reduce(
		(acc, key) => {
			acc[key] = key;
			return acc;
		},
		{} as { [key: string]: string }
	);
};

export const removePrefixCamelCase = (str: string): string => {
	const firstUpperCaseIndex = str.slice(1).search(/[A-Z]/) + 1;

	if (firstUpperCaseIndex <= 0) return str;

	const result = str.slice(firstUpperCaseIndex);

	return result.charAt(0).toLowerCase() + result.slice(1);
};

/**

Function to merge multiple refs into a single ref.
@param refs - The refs to merge.
@returns Merged ref object.
*/
export function mergeRefs<T>(
	...refs: Array<
		RefObject<T> | ((instance: T | null) => void) | null | undefined
	>
): RefObject<T> {
	const mergedRef = useRef<T>(null);
	useEffect(() => {
		refs.forEach((ref) => {
			if (!ref) return;

			if (typeof ref === 'function') {
				ref(mergedRef.current);
			} else {
				Object.assign(ref, { current: mergedRef.current });
			}
		});
	}, [refs]);
	return mergedRef;
}

export const debounce = (func: (...args: any[]) => void, delay: number) => {
	let timer: NodeJS.Timeout;
	return (...args: any[]) => {
		clearTimeout(timer);
		timer = setTimeout(() => func(...args), delay);
	};
};

export const throttle = (func: (...args: any[]) => void, delay: number) => {
	let lastCall = 0;
	return (...args: any[]) => {
		const now = Date.now();
		if (now - lastCall >= delay) {
			lastCall = now;
			func(...args);
		}
	};
};

export const convertUnitTypeCodeToName = (unitType: string): string => {
	const found = UNIT_TYPES.find((e) => e?.value === unitType);
	return found ? found?.label : unitType;
};

export const convertProductionTypeCodeToName = (
	productionType: string
): string => {
	const options = PRODUCTION_TYPE_AGRICULTURE_OPTIONS.concat(
		PRODUCTION_TYPE_FISHING_OPTIONS
	);
	const found = options.find((e) => e?.value === productionType);
	return found ? found?.label : productionType;
};

export const convertProductionUnitCodeToName = (
	productionUnit: string
): string => {
	const options = PRODUCTION_UNIT_AGRICULTURE_OPTIONS.concat(
		PRODUCTION_UNIT_FISHING_OPTIONS
	);
	const found = options.find((e) => e?.value === productionUnit);
	return found ? found?.label : productionUnit;
};

export const convertNameSelectMDefault = (type?: MasterInfoEnum) => {
	switch (type) {
		case MasterInfoEnum.PROGRAM:
			return 'all-program';
		case MasterInfoEnum.APPLICANT_BY_FRANCHISE:
			return '근로자 전체';
		case MasterInfoEnum.DISTRICT:
		case MasterInfoEnum.PUBLIC_FRANCHISE_AGRICULTURE:
			return 'select';
		default:
			return 'all';
	}
};

/**
 * @description Checks if all specified keys exist in the object.
 * @param {Array<string>} keys - The list of keys to check.
 * @param {Object} obj - The object in which to check for the keys.
 * @returns {boolean} Returns true if all keys are present in the object, otherwise false.
 */
export const hasAllKeys = (keys: string[], obj: object): boolean =>
	all((key) => has(key, obj), keys);

/**
 * @description Checks if any of the specified keys exist in the object.
 * @param {Array<string>} keys - The list of keys to check.
 * @param {Object} obj - The object in which to check for the keys.
 * @returns {boolean} Returns true if any of the keys are present in the object, otherwise false.
 */
export const hasAnyKey = (keys: string[], obj: object): boolean =>
	any((key) => has(key, obj), keys);

type Obj = Record<string, any>;
export const omitSameValue = (source: Obj, target: Obj): Obj =>
	Object.fromEntries(
		Object.entries(target).filter(([key, val]) => !equals(source[key], val))
	);

export const convertFranchiseTypeCodeToName = (
	businessType: string
): string => {
	const found = BUSINESS_TYPE_OPTIONS.find((e) => e?.value === businessType);
	return found ? found?.label : businessType;
};

export const convertFranchiseTypeDetailCodeToName = (
	franchiseType: string
): string => {
	const options = [
		...AGRI_FRANCHISE_OPTIONS,
		...FISHERY_FRANCHISE_OPTIONS,
	] as const;
	const found = options.find((e) => e?.value === franchiseType);
	return found ? found?.label : franchiseType;
};

export const convertRecommenderTypeCodeToName = (
	recommenderType: string
): string => {
	const found = DIVISION_RECOMMENDER_OPTIONS.find(
		(e) => e?.value === recommenderType
	);
	return found ? found?.label : recommenderType;
};

export const sortByFieldAndLanguage = <T extends Record<string, any>>(
	field: keyof T,
	language = 'ko'
) => {
	return (a: T, b: T): number => {
		return a[field]?.toString().localeCompare(b[field]?.toString(), language);
	};
};

export const convertDivisionRecommender = (type: string): string => {
	const found = DIVISION_RECOMMENDER_OPTIONS.find((e) => e?.value === type);
	return found?.label ?? type;
};

export const equalsArrayNumber = (currentIds: number[], ids: number[]) => {
	if (currentIds?.length !== ids?.length) return false;
	const sortedArr1 = currentIds?.sort((x, y) => x - y);
	const sortedArr2 = ids?.sort((x, y) => x - y);

	return sortedArr1?.every((v, i) => v === sortedArr2?.[i]);
};

export const convertFileType = (type: string): string => {
	const found = FILE_TYPE_OPTIONS.find((e) => e?.value === type);
	return found?.label ?? type;
};

export const JoinRelatedInformation = (names: string[]) => {
	if (isNilOrEmpty(names)) return '';
	return names.join(', ');
};

export const convertDocumentType = (type: string): string => {
	if (isNilOrEmpty(type)) return '';
	const found = ALL_DOCUMENT_OPTIONS.find((e) => e?.value === type);
	return (found?.label as string) ?? type;
};

export const updateJsonString = (
	jsonString: string,
	newValue: Record<string, any>
) => {
	try {
		const data = JSON.parse(jsonString);
		for (const [key, value] of Object.entries(newValue)) {
			if (Object.prototype.hasOwnProperty.call(data, key)) {
				data[key] = value;
			}
		}
		return JSON.stringify(data);
	} catch (error) {
		return jsonString;
	}
};

export const convertRelationship = (type: string): string => {
	const found = RELATIONSHIP_OPTIONS.find((e) => e?.value === type);
	return found?.label ?? type;
};

export const convertProviderTableType = (type: string): string => {
	const found = PROVIDERS_TYPE_LODGING_OPTIONS.find((e) => e?.value === type);
	return found?.label ?? type;
};

export const convertDocumentTypeValue = (documentType: string): string => {
	if (!documentType) return '';
	const found = ALL_DOCUMENT_OPTIONS.find((e) => e?.label === documentType);
	return found ? String(found?.value) : documentType;
};

export const convertRangeAgeEnumToText = (rangeAge: RangeAgeEnum): string => {
	if (isNilOrEmpty(rangeAge)) return '';
	const found = RANGE_AGE_OPTIONS.find((e) => e?.value === rangeAge);
	return found?.label ?? rangeAge;
};

export const isTablet = window.innerWidth < 1550;
export const getTranslatedMessage = <NS extends Namespace>(
	t: TFunction<NS>,
	key: ReactNode,
	options?: {
		ns?: NS;
		defaultValue?: ReactNode;
		options?: TOptions;
	}
) => {
	if (isString(key) && exists(key, { ns: options?.ns ?? 'common' })) {
		return (t as (key: string, options?: TOptions) => string)(key, {
			...options?.options,
		});
	}
	return options?.defaultValue ?? key;
};

export const showAddress = (address: string, addressDetail: string) => {
	if (isNilOrEmpty(address) && isNilOrEmpty(addressDetail)) return '';

	if (isNilOrEmpty(address)) return addressDetail;
	if (isNilOrEmpty(addressDetail)) return address;

	return `${address}, ${addressDetail}`;
};

export const getWorkderApplicantStatus = (params: {
	programEndDate: string;
	status: AppProgramStatusEnum;
}) => {
	const { programEndDate, status } = params;

	const isExpiredProgram = dayjs(
		programEndDate,
		SUPPORT_FORMAT_DATE_LIST
	).isBefore(dayjs(), 'day');

	if (isExpiredProgram) {
		return status === AppProgramStatusEnum.APPROVED
			? '프로그램 참여 종료'
			: '신청기간만료';
	}

	return AppProgramLabel[status] || '-';
};

export const renderTargetMenu = (menu: AreaRefTableNameEnum) => {
	switch (menu) {
		case AreaRefTableNameEnum.APPLICANT:
			return '근로자';
		case AreaRefTableNameEnum.FRANCHISE:
			return '사업장';
		case AreaRefTableNameEnum.RECOMMENDER:
			return '추천자';
		case AreaRefTableNameEnum.LODGING:
			return '숙소 주소';
		case AreaRefTableNameEnum.UNIT:
			return '사업체';
		case AreaRefTableNameEnum.ASSIGNMENT_FRANCHISE:
			return '배정 사업장';
		case AreaRefTableNameEnum.WORK_CHANGE_PLACE:
			return '원 배정 정보';
		case AreaRefTableNameEnum.UNAUTHORIZED_DEPARTURE:
			return '사업장 정보';
		default:
			return '';
	}
};

export const renderTargetMenuTable = (menu: AreaRefTableNameEnum) => {
	switch (menu) {
		case AreaRefTableNameEnum.APPLICANT:
			return '근로자';
		case AreaRefTableNameEnum.FRANCHISE:
			return '사업장';
		case AreaRefTableNameEnum.RECOMMENDER:
			return '추천자';
		case AreaRefTableNameEnum.LODGING:
			return '숙소';
		case AreaRefTableNameEnum.UNIT:
			return '사업체';
		case AreaRefTableNameEnum.ASSIGNMENT_FRANCHISE:
			return '배정';
		case AreaRefTableNameEnum.WORK_CHANGE_PLACE:
			return '근무처 변경';
		case AreaRefTableNameEnum.UNAUTHORIZED_DEPARTURE:
			return '무단이탈';
		default:
			return '';
	}
};

export const hexToHSL = (hex: string) => {
	hex = hex.replace('#', '');
	let r = parseInt(hex.substring(0, 2), 16) / 255;
	let g = parseInt(hex.substring(2, 4), 16) / 255;
	let b = parseInt(hex.substring(4, 6), 16) / 255;

	const max = Math.max(r, g, b);
	const min = Math.min(r, g, b);
	let h,
		s,
		l = (max + min) / 2;

	if (max === min) {
		h = s = 0; // xám
	} else {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r:
				h = (g - b) / d + (g < b ? 6 : 0);
				break;
			case g:
				h = (b - r) / d + 2;
				break;
			case b:
				h = (r - g) / d + 4;
				break;
		}
		if (typeof h === 'undefined') {
			h = 0;
		}
		h *= 60;
	}
	return { h, s: s * 100, l: l * 100 };
};

export const generateColors = (count: number, baseColor = '#0088FE') => {
	const { h, s, l } = hexToHSL(baseColor);
	const colors = [];
	for (let i = 0; i < count; i++) {
		const hue = (h + i * (360 / count)) % 360;
		colors.push(`hsl(${hue}, ${s}%, ${l}%)`);
	}
	return colors;
};

/**
 * @description Composes the other value from the text and token.
 * @param text - The text to compose.
 * @param token - The token to use for composing.
 * @returns The composed value.
 */
export const composeOtherValue = (
	text: string,
	token = MidTermDepartureEnum.OTHER as string
): string => {
	const trimmed = String(text ?? '').trim();
	return trimmed ? `${token} (${trimmed})` : token;
};

/**
 * @description Parses the other value from the raw string.
 * @param raw - The raw string to parse.
 * @param token - The token to use for parsing.
 * @returns An object containing the hasOther flag and the text value.
 */
export const parseOtherValue = (
	raw: any,
	token = MidTermDepartureEnum.OTHER as string
) => {
	const str = String(raw ?? '');
	const safeToken = token.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
	const tokenOnly = new RegExp(`^\\s*${safeToken}\\s*$`).test(str);
	const match = str.match(new RegExp(`^\\s*${safeToken}\\s*\\((.*?)\\)\\s*$`));
	return {
		hasOther: tokenOnly || !!match,
		text: match ? match[1]?.trim() : '',
	};
};

export const calculateRelaxTimeWithBreaks = (
	hour: number = 0,
	min: number = 0,
	breaksNumber: number = 1
) => {
	const minutesPerBreak = Math.max(0, hour * 60 + min);
	const totalMinutes = minutesPerBreak / Math.max(1, breaksNumber);
	const totalHours = totalMinutes / 60;
	return Math.floor(totalHours * 2) / 2;
};

export const getRelaxTimeOptionsMoreThanOrEqual = (maxHour?: number | null) => {
	if (isNilOrEmpty(maxHour)) return [];

	if (Number.isNaN(Number(maxHour))) return RELAX_TIME_OPTIONS;

	return RELAX_TIME_OPTIONS.filter(
		(option) => Number(option.value) >= Number(maxHour)
	);
};

/**
 * @description Generates a consistent color for a public franchise organization based on its ID
 * @param publicFranchiseId - The public franchise ID
 * @returns HSL color string with good contrast for black text
 */
export const generateOrganizationColor = (
	publicFranchiseId: number
): string => {
	if (!publicFranchiseId) return '#F1F6FF'; // Default color

	// Use the ID to generate a consistent hue
	const hue = (publicFranchiseId * 137.5) % 360; // Golden angle for good distribution

	// Use moderate saturation and high lightness for good text contrast
	const saturation = 60; // Moderate saturation
	const lightness = 85; // High lightness for good contrast with black text

	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * @description Generates a hover color for organization events
 * @param publicFranchiseId - The public franchise ID
 * @returns HSL color string for hover state
 */
export const generateOrganizationHoverColor = (
	publicFranchiseId: number
): string => {
	if (!publicFranchiseId) return '#CDDFFF'; // Default hover color

	const hue = (publicFranchiseId * 137.5) % 360;
	const saturation = 70; // Higher saturation for hover
	const lightness = 75; // Lower lightness for hover effect

	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

/**
 * @description Generates a border color for organization events
 * @param publicFranchiseId - The public franchise ID
 * @returns HSL color string for border
 */
export const generateOrganizationBorderColor = (
	publicFranchiseId: number
): string => {
	if (!publicFranchiseId) return '#6782F9'; // Default border color

	const hue = (publicFranchiseId * 137.5) % 360;
	const saturation = 80; // High saturation for border
	const lightness = 60; // Medium lightness for border

	return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
};

// Helper: lighten an hsl(h, s%, l%) string by increasing lightness
export const lightenHsl = (hslColor: string, deltaL: number) => {
	const match = /hsl\(\s*(\d+)\s*,\s*(\d+)%\s*,\s*(\d+)%\s*\)/i.exec(hslColor);
	if (!match) return hslColor;
	const h = Number(match[1]);
	const s = Number(match[2]);
	const l = Number(match[3]);
	const nl = Math.max(0, Math.min(100, l + deltaL));
	return `hsl(${h}, ${s}%, ${nl}%)`;
};

/**
 * @description Convert HSL values to RGB (0-255)
 */
const hslToRgb = (
	h: number,
	s: number,
	l: number
): { r: number; g: number; b: number } => {
	s /= 100;
	l /= 100;
	const c = (1 - Math.abs(2 * l - 1)) * s;
	const hh = h / 60;
	const x = c * (1 - Math.abs((hh % 2) - 1));
	let r1 = 0;
	let g1 = 0;
	let b1 = 0;
	if (hh >= 0 && hh < 1) {
		r1 = c;
		g1 = x;
		b1 = 0;
	} else if (hh >= 1 && hh < 2) {
		r1 = x;
		g1 = c;
		b1 = 0;
	} else if (hh >= 2 && hh < 3) {
		r1 = 0;
		g1 = c;
		b1 = x;
	} else if (hh >= 3 && hh < 4) {
		r1 = 0;
		g1 = x;
		b1 = c;
	} else if (hh >= 4 && hh < 5) {
		r1 = x;
		g1 = 0;
		b1 = c;
	} else {
		r1 = c;
		g1 = 0;
		b1 = x;
	}
	const m = l - c / 2;
	return {
		r: Math.round((r1 + m) * 255),
		g: Math.round((g1 + m) * 255),
		b: Math.round((b1 + m) * 255),
	};
};

/**
 * @description Parse color string (supports hsl(...) and #rrggbb) to RGB
 */
const parseColorToRgb = (
	color: string
): { r: number; g: number; b: number } | null => {
	const hslMatch =
		/hsl\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*\)/i.exec(
			color
		);
	if (hslMatch) {
		const h = Number(hslMatch[1]);
		const s = Number(hslMatch[2]);
		const l = Number(hslMatch[3]);
		return hslToRgb(h, s, l);
	}
	const hexMatch = /^#([0-9a-f]{6})$/i.exec(color.trim());
	if (hexMatch) {
		const intVal = parseInt(hexMatch[1], 16);
		return {
			r: (intVal >> 16) & 255,
			g: (intVal >> 8) & 255,
			b: intVal & 255,
		};
	}
	return null;
};

/**
 * @description Convert RGB (0-255) to HSL (0-360, 0-100, 0-100)
 */
const rgbToHsl = (
	r: number,
	g: number,
	b: number
): { h: number; s: number; l: number } => {
	const r1 = r / 255;
	const g1 = g / 255;
	const b1 = b / 255;
	const max = Math.max(r1, g1, b1);
	const min = Math.min(r1, g1, b1);
	let h = 0;
	let s = 0;
	const l = (max + min) / 2;
	if (max !== min) {
		const d = max - min;
		s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
		switch (max) {
			case r1:
				h = (g1 - b1) / d + (g1 < b1 ? 6 : 0);
				break;
			case g1:
				h = (b1 - r1) / d + 2;
				break;
			default:
				h = (r1 - g1) / d + 4;
		}
		h /= 6;
	}
	return {
		h: Math.round(h * 360),
		s: Math.round(s * 100),
		l: Math.round(l * 100),
	};
};

/**
 * @description Parse color to HSL. Supports hsl(...) and #rrggbb
 */
const parseColorToHsl = (
	color: string
): { h: number; s: number; l: number } | null => {
	const hslMatch =
		/hsl\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*\)/i.exec(
			color
		);
	if (hslMatch) {
		return {
			h: Number(hslMatch[1]),
			s: Number(hslMatch[2]),
			l: Number(hslMatch[3]),
		};
	}
	const hexMatch = /^#([0-9a-f]{6})$/i.exec(color.trim());
	if (hexMatch) {
		const intVal = parseInt(hexMatch[1], 16);
		const rgb = {
			r: (intVal >> 16) & 255,
			g: (intVal >> 8) & 255,
			b: intVal & 255,
		};
		return rgbToHsl(rgb.r, rgb.g, rgb.b);
	}
	return null;
};

/**
 * @description Make a pastel/lighter version of color by increasing lightness and capping saturation
 */
export const pastelizeColor = (
	color: string,
	targetLightness: number = 95,
	maxSaturation: number = 45
): string => {
	const hsl = parseColorToHsl(color);
	if (!hsl) return color;
	const newS = Math.max(0, Math.min(100, Math.min(hsl.s, maxSaturation)));
	const newL = Math.max(0, Math.min(100, targetLightness));
	return `hsl(${Math.round(hsl.h)}, ${Math.round(newS)}%, ${Math.round(newL)}%)`;
};

/**
 * @description Calculate relative luminance per WCAG
 */
const relativeLuminance = (r: number, g: number, b: number): number => {
	const srgb = [r, g, b].map((v) => v / 255);
	const [rl, gl, bl] = srgb.map((c) =>
		c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
	);
	return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
};

/**
 * @description Contrast ratio of two RGB colors
 */
const contrastRatio = (
	rgb1: { r: number; g: number; b: number },
	rgb2: { r: number; g: number; b: number }
): number => {
	const L1 = relativeLuminance(rgb1.r, rgb1.g, rgb1.b);
	const L2 = relativeLuminance(rgb2.r, rgb2.g, rgb2.b);
	const [hi, lo] = L1 >= L2 ? [L1, L2] : [L2, L1];
	return (hi + 0.05) / (lo + 0.05);
};

/**
 * @description Given a background color, return a readable text color.
 * Prefers keeping the same hue by darkening until it reaches the target contrast.
 * Falls back to black/white which yields higher contrast if needed.
 */
export const getAccessibleTextColor = (
	bgColor: string,
	minContrast: number = 4.5
): string => {
	const bgRgb = parseColorToRgb(bgColor);
	if (!bgRgb) return '#222222';

	// Try to keep the same hue by darkening
	const hslMatch =
		/hsl\(\s*(\d+(?:\.\d+)?)\s*,\s*(\d+(?:\.\d+)?)%\s*,\s*(\d+(?:\.\d+)?)%\s*\)/i.exec(
			bgColor
		);
	if (hslMatch) {
		const h = Number(hslMatch[1]);
		const s = Number(hslMatch[2]);
		const l = Number(hslMatch[3]);
		for (let delta = 10; delta <= 70; delta += 5) {
			const candidate = hslToRgb(
				h,
				Math.min(80, s + 10),
				Math.max(0, l - delta)
			);
			const ratio = contrastRatio(candidate, bgRgb);
			if (ratio >= minContrast) {
				return `hsl(${h}, ${Math.min(80, s + 10)}%, ${Math.max(0, l - delta)}%)`;
			}
		}
	}

	// Fallback to black or white depending on contrast
	const black = { r: 0, g: 0, b: 0 };
	const white = { r: 255, g: 255, b: 255 };
	const blackRatio = contrastRatio(black, bgRgb);
	const whiteRatio = contrastRatio(white, bgRgb);
	return blackRatio >= whiteRatio ? '#111111' : '#FFFFFF';
};
